<template>
  <div class="c-select-seat-type">
    <BittsRadioGroupCards
      data-testid="seat-type-selector"
      form-label="Seat Type"
      class="mb-16"
      :orientation="orientation"
      :options="options"
      :initial-value="pickedSeat?.value"
      @change="handlePickSeat"
    >
      <template #right="{ option }">
        <component
          v-if="Object.values(option.ctaProps || {}).length"
          :is="option.ctaProps.tooltipText ? BittsTooltip : 'div'"
          placement="bottomRight"
        >
          <BittsButton
            v-bind="option.ctaProps"
            size="x-small"
            @click="option.ctaProps.click"
          />
          <template #title>
            {{ option.ctaProps.tooltipText }}
          </template>
        </component>
      </template>
      <template #tooltip="{ option }">
        {{ tooltipText(option.value) }}
      </template>
    </BittsRadioGroupCards>
  </div>
</template>
<script setup>
import {
  BittsButton,
  BittsRadioGroupCards,
  BittsTooltip,
} from '@crossbeam/bitts';

import { pluralize } from 'humanize-plus';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';

import useTeamUpsellCTAs from '@/composables/team/useTeamUpsellCTAs';
import useAlerts from '@/composables/useAlerts';
import useHasFeature from '@/composables/useHasFeature';
import useSeats from '@/composables/useSeats';
import { PAST_DUE } from '@/constants/billing';
import { CORE, INTEGRATION } from '@/constants/team';
import { SALES, V4_SEAT_OPTIONS } from '@/constants/team_v4';
import { useBillingStore } from '@/stores';

const props = defineProps({
  auths: {
    type: Array,
    default: () => [],
  },
  pickedSeat: {
    type: Object,
    default: null,
  },
  isEditingUser: {
    type: Boolean,
    default: false,
  },
  orientation: {
    type: String,
    default: 'vertical',
    validator: (value) => ['horizontal', 'vertical'].includes(value),
  },
  disableSeatChecks: {
    type: Boolean,
    default: false,
  },
  allowIntegrationSeat: {
    type: Boolean,
    default: true,
  },
});
const emit = defineEmits([
  'seat-selected',
  'cta-clicked',
  'seats-needed',
  'open-add-seats-modal',
]);
const billingStore = useBillingStore();
const { isSubscriptionCancelled, isSupernodeOrEnterpriseTier } =
  storeToRefs(billingStore);
const { billingAlert } = useAlerts();
const { seatsRemainingMap, canUseIntegrationSeats } = useSeats();

/* If we are editing the current user, then we do not want to count
them toward the seat limit */
const { coreUserCount, salesUserCount, integrationUserCount } =
  props.auths.reduce(
    (agg, auth) => {
      if (!props.isEditingUser) return agg;
      if (auth?.seat_type === CORE) agg.coreUserCount += 1;
      else if (auth?.seat_type === INTEGRATION) agg.integrationUserCount += 1;
      else agg.salesUserCount += 1;
      return agg;
    },
    { coreUserCount: 0, salesUserCount: 0, integrationUserCount: 0 },
  );

/* Must be computed, not ref: seatsRemainingMap is a reactive computed from the billing store.
   A ref would capture a one-time snapshot and go stale after the billing store refreshes. */
const adjustedSeatsRemaining = computed(() => ({
  [CORE]: seatsRemainingMap.value[CORE] + coreUserCount,
  [SALES]: seatsRemainingMap.value[SALES] + salesUserCount,
  [INTEGRATION]: seatsRemainingMap.value[INTEGRATION] + integrationUserCount,
}));

/* Seats remaining map */
function handlePickSeat(val) {
  const seat = options.value.find((option) => option.value === val);
  emit('seat-selected', seat);
}

const { canGoOverSeatQuota } = useHasFeature();
const isPastDue = computed(() => billingAlert.value?.type === PAST_DUE);
/* allowIntegrationSeat is false in SSOConfigForm for now: integration seats are
   for machine/automation users, not for SSO-provisioned human users, so they
   should not appear as a default SSO seat type at this stage */
const showIntegrationOption = computed(
  () => props.allowIntegrationSeat && canUseIntegrationSeats.value,
);
/* Builds the seat type radio card options list: filters out Integration unless
   the feature flag and tier allow it, adjusts descriptions by tier, and applies
   disabled state + upsell CTAs when the org is at seat quota */
const options = computed(() => {
  // Deep-clone V4_SEAT_OPTIONS so mutations below don't affect the source constant
  const opts = JSON.parse(JSON.stringify(V4_SEAT_OPTIONS))
    // Hide Integration unless the feature flag is on and the org is on a qualifying tier
    .filter(
      (option) => option.value !== INTEGRATION || showIntegrationOption.value,
    )
    // Swap in the connector-tier description for Sales when the org is not supernode/enterprise
    .map((option) => {
      if (option.value === SALES) {
        return {
          ...option,
          description: isSupernodeOrEnterpriseTier.value
            ? option.description
            : option.descriptionConnector,
        };
      }
      return option;
    });

  // Admins who can exceed quota (or when seat checks are bypassed) see all options enabled
  if (canGoOverSeatQuota.value || props.disableSeatChecks) return opts;

  return opts.map((option) => ({
    ...option,
    disabled: isOptionDisabled(option.value),
    ctaProps: outOfSeats(option.value) ? getCta(option.value) : {},
  }));
});

/* CTA States */
const { getButtonInfo, getIntegrationButtonInfo } = useTeamUpsellCTAs();
function getCta(seatType) {
  if (seatType === INTEGRATION) return getIntegrationButtonInfo(true);
  return getButtonInfo(seatType === SALES, true, emit);
}

function showCta(seatType) {
  if (isSubscriptionCancelled.value || props.disableSeatChecks) return false;
  const showingCta = outOfSeats(seatType);
  if (showingCta) emit('seats-needed', seatsNeeded(seatType));
  return showingCta;
}

/* If this is an edit modal, we only show the CTA if there are fewer seats remaining
  of the opposite type than the number of auths. If we are sending invites, we show it
  if we run out of auths of the given type, or there were not any auths of that type to
  begin with */
function outOfSeats(seatType) {
  if (canGoOverSeatQuota.value) return false;
  return props.isEditingUser
    ? adjustedSeatsRemaining.value[seatType] < props.auths.length
    : seatsRemainingMap.value[seatType] - props.auths.length + 1 <= 0 ||
        seatsRemainingMap.value[seatType] === 0;
}

function isOptionDisabled(seatType) {
  if (isPastDue.value) return true;
  if (outOfSeats(seatType)) return true; // User has cancelled, and they have no more seats of this type available
  return showCta(seatType) && !(props.pickedSeat.value === seatType);
}

function seatsNeeded(seatType) {
  return Math.max(
    props.auths.length - adjustedSeatsRemaining.value[seatType],
    1,
  );
}

/* CTA is showing */
function tooltipText(seatType) {
  if (seatType === INTEGRATION && isOptionDisabled(seatType))
    return 'Talk to sales for more integration seats';
  if (isOptionDisabled(seatType))
    return `You have no more ${seatType === SALES ? 'Sales' : 'Full Access'} seats`;
  return `You need ${seatsNeeded(seatType)} more ${pluralize(seatsNeeded(seatType), 'seat')} to make this change`;
}
</script>
